import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, from, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, timeout } from 'rxjs/operators';

/** Roles that share the same workspace permissions as admin (manager = admin). */
export const ADMIN_EQUIVALENT_ROLES = ['admin', 'SuperAdmin', 'manager'] as const;

@Injectable({
  providedIn: 'root'
})
export class RoleService {

  constructor(
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore
  ) {}

  /**
   * One Firestore read after Firebase auth has restored.
   * Same source UserRoleAuthGuard uses for route checks.
   */
  getSignedInProfile(): Observable<{
    uid: string;
    role: string | null;
    userParentId?: string;
  } | null> {
    return from(this.afAuth.currentUser).pipe(
      switchMap((user) => {
        if (user) {
          return of(user);
        }
        return this.afAuth.authState.pipe(
          filter((restored) => !!restored),
          take(1),
          timeout(4000),
          catchError(() => of(null)),
        );
      }),
      switchMap((user) => {
        if (!user) {
          return of(null);
        }
        return this.afs.doc(`users/${user.uid}`).valueChanges().pipe(
          take(1),
          map((userData: any) => ({
            uid: user.uid,
            role: userData?.role ?? null,
            userParentId: userData?.userParentId,
          })),
        );
      }),
    );
  }

  getRole(): Observable<string | null> {
    return this.afAuth.authState.pipe(
      switchMap(user => {
        if (!user?.uid) {
          return of(null);
        }
        return this.afs.doc(`users/${user.uid}`).valueChanges();
      }),
      map((userData: any) => userData?.role ?? null)
    );
  }

  /** True for admin, SuperAdmin, and manager (same access). */
  isAdminEquivalent(): Observable<boolean> {
    return this.getRole().pipe(
      map(role => RoleService.isAdminEquivalentRole(role))
    );
  }

  canEdit(): Observable<boolean> {
    return this.isAdminEquivalent();
  }

  isAnalyst(): Observable<boolean> {
    return this.getRole().pipe(
      map(role => role === 'analyst')
    );
  }

  isViewer(): Observable<boolean> {
    return this.getRole().pipe(
      map(role => role === 'viewer')
    );
  }

  static isAdminEquivalentRole(role: string | null | undefined): boolean {
    return !!role && (ADMIN_EQUIVALENT_ROLES as readonly string[]).includes(role);
  }
}
